import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from './useMediaQuery';
import { triggerHaptic } from './useHaptics';

/**
 * Custom hook for swipe-back navigation gesture on mobile
 * Mimics iOS/Android native back gesture from left edge
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether swipe-back is enabled (default: true)
 * @param {number} options.edgeThreshold - Distance from left edge to trigger (default: 20px)
 * @param {number} options.swipeThreshold - Minimum swipe distance to navigate back (default: 100px)
 * @returns {Object} - { swipeProgress, isSwipingBack }
 */
interface SwipeBackOptions {
  enabled?: boolean;
  edgeThreshold?: number;
  swipeThreshold?: number;
  onSwipeBack?: () => void;
}

export function useSwipeBack(options: SwipeBackOptions = {}) {
  const {
    enabled = true,
    edgeThreshold = 20,
    swipeThreshold = 100,
  } = options;

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwipingBack, setIsSwipingBack] = useState(false);
  const touchStartRef = useRef<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  // Use refs for values read inside event handlers so the effect doesn't
  // need to re-register listeners when they change every frame.
  const swipingRef = useRef(false);
  const progressRef = useRef(0);

  useEffect(() => {
    if (!isMobile || !enabled) {
      return;
    }

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      
      if (touch.clientX <= edgeThreshold) {
        touchStartRef.current = touch.clientY;
        touchStartXRef.current = touch.clientX;
        swipingRef.current = true;
        setIsSwipingBack(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!swipingRef.current || touchStartXRef.current === null) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartXRef.current;
      const deltaY = Math.abs(touch.clientY - (touchStartRef.current ?? 0));

      if (deltaY < deltaX && deltaX > 0) {
        e.preventDefault();
        
        const progress = Math.min(deltaX / swipeThreshold, 1);
        progressRef.current = progress;
        setSwipeProgress(progress);
      } else if (deltaY > 30) {
        swipingRef.current = false;
        progressRef.current = 0;
        setIsSwipingBack(false);
        setSwipeProgress(0);
        touchStartRef.current = null;
        touchStartXRef.current = null;
      }
    };

    const handleTouchEnd = () => {
      if (!swipingRef.current) return;

      if (progressRef.current >= 1) {
        triggerHaptic('success');
        navigate(-1);
      }

      swipingRef.current = false;
      progressRef.current = 0;
      setIsSwipingBack(false);
      setSwipeProgress(0);
      touchStartRef.current = null;
      touchStartXRef.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isMobile, enabled, edgeThreshold, swipeThreshold, navigate]);

  return {
    swipeProgress,
    isSwipingBack,
  };
}
